import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { BOOK_COMPLIANCE, type BookCompliance } from '../lib/bookAsk'
import type { BookFilterControls } from '../lib/useBookFilter'
import { FormField, FormSelect } from './FormField'

/**
 * A book's search and filters (TRKC-97, Sergio's ask): the search, sent with its button as
 * it always was, then the paperwork's standing, the working relationship and the state the
 * address is in, applied the moment they change. One bar for both books, so the broker and
 * carrier books cannot drift apart on what they narrow by; the standings read as the badges
 * in the table read, so a filter and the rows it finds use the same words.
 */
export function BookFilterBar({
  book,
  controls,
  isLoading,
}: {
  book: 'brokers' | 'carriers'
  controls: BookFilterControls
  isLoading: boolean
}) {
  const { t } = useTranslation()
  const { filter, searchText, setSearchText, stateText, typeState, narrow } = controls

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    narrow({})
  }

  return (
    <form className="toolbar toolbar--filters" role="search" onSubmit={handleSearch}>
      <label className="field">
        <span>{t(`${book}.search`)}</span>
        <input
          type="search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder={t(`${book}.searchPlaceholder`)}
        />
      </label>
      <button type="submit" className="button button--quiet" disabled={isLoading}>
        {t(`${book}.searchAction`)}
      </button>
      <FormSelect
        name="complianceFilter"
        label={t('books.filters.compliance')}
        value={filter.compliance ?? ''}
        onChange={(value) => narrow({ compliance: value === '' ? undefined : (value as BookCompliance) })}
        options={[
          { value: '', label: t('books.filters.any') },
          ...BOOK_COMPLIANCE.map((standing) => ({ value: standing, label: t(`compliance.${standing.toLowerCase()}`) })),
        ]}
      />
      {/* Inactive records are hidden unless asked for (TRKC-110 item 2): Active is the default. */}
      <FormSelect
        name="relationshipFilter"
        label={t('books.filters.relationship')}
        value={filter.active === undefined ? 'all' : filter.active ? 'active' : 'inactive'}
        onChange={(value) => narrow({ active: value === 'all' ? undefined : value === 'active' })}
        options={[
          { value: 'active', label: t('books.filters.active') },
          { value: 'inactive', label: t('books.filters.inactive') },
          { value: 'all', label: t('books.filters.all') },
        ]}
      />
      <FormField
        name="stateFilter"
        label={t('books.filters.state')}
        value={stateText}
        onChange={typeState}
        maxLength={2}
        placeholder="TX"
        width="narrow"
      />
    </form>
  )
}
